import dayjs from 'dayjs';
import { saveAs } from 'file-saver';

import ApiGateway, { ApiFacetsResponse, CustomAxiosResponse } from '@app/helpers/AxiosHelper';

import { Lead } from '@app/store/customers/customers.store';

import { ApiResponse } from '../apv/order.type';
import { ListingApiResponse } from '../common.types';
import { ApiLeadReason } from '../customers/customer.type';

import { LeadCounterParams, LeadCounterType, LeadsFacets } from './lead.type';

const { REACT_APP_LEAD_URL } = process.env;

interface LeadSearchArgs {
    limit: number;
    page: number;
    leadType: string[];
    leadTypeGroup: string[];
    leadSource: string[];
    leadStatus: string[];
    contactType: string[];
    created: string;
    createdBy: string;
    dealershipId: string;
    dealership: string;
    user: string;
    contact: string;
    vehicleIdentifier: string;
    noLeadParent: boolean;
    flags: string[];
    sort: string;
    receiptedPeriod: string;
    scheduleAppointmentPeriod: string;
    contactZipcode: string;
    status: string;
}

export function getAllLeads(params: object) {
    return ApiGateway.get<
        ApiFacetsResponse<Lead[], LeadsFacets>,
        CustomAxiosResponse<ApiFacetsResponse<Lead[], LeadsFacets>>
    >(`${REACT_APP_LEAD_URL}/lead`, {
        params,
    });
}

export function getLeadByStatus(data: LeadSearchArgs) {
    const status = data?.status;
    delete data?.status;

    if (data?.createdBy) {
        data.createdBy = data.createdBy.trim();
    }

    const filteredData = Object.fromEntries(
        Object.entries(data).filter(
            ([_, value]) => value !== null && value !== undefined && !(Array.isArray(value) && value.length === 0),
        ),
    );
    return ApiGateway.post<
        ListingApiResponse<Lead[], LeadsFacets>,
        CustomAxiosResponse<ListingApiResponse<Lead[], LeadsFacets>>
    >(`${REACT_APP_LEAD_URL}/lead/status/${status}`, {
        ...filteredData,
    });
}

export function getLeadReason() {
    return ApiGateway.get<ApiResponse<ApiLeadReason>, CustomAxiosResponse<ApiResponse<ApiLeadReason>>>(
        `${REACT_APP_LEAD_URL}/lead-reason`,
    );
}

export async function getLeadCounter(counterType: LeadCounterType, params?: LeadCounterParams): Promise<number> {
    const { result } = await ApiGateway.get<ApiResponse<number>, CustomAxiosResponse<ApiResponse<number>>>(
        `${REACT_APP_LEAD_URL}/counter/lead/${counterType}`,
        { params },
    );
    return result;
}

export function getExportedLeads(startDate?: string, endDate?: string) {
    const params = startDate !== null && endDate !== null ? `?startDate=${startDate}&endDate=${endDate}` : '';

    return ApiGateway.get<string, BlobPart>(`${REACT_APP_LEAD_URL}/lead/export/closed-lead-fid${params}`, {
        responseType: 'arraybuffer',
    }).then((response) => {
        const blob = new Blob([response], {
            type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
        });
        saveAs(blob, `closed-leads-fid-export-${dayjs().format('YYYY-MM-DD')}.xlsx`);
    });
}

interface LeadFinance {
    financialType: string;
    financialAgency: string;
    financialBudget: number;
    financialInput: number;
    financialRent: number;
    comment: string;
    interest: string;
}

export function addLeadFinance(leadId: string, data: LeadFinance) {
    return ApiGateway.post(`${REACT_APP_LEAD_URL}/lead/${leadId}/finance`, data);
}

export function updateFinance(leadId: string, id: string, data: LeadFinance) {
    return ApiGateway.put(`${REACT_APP_LEAD_URL}/lead/${leadId}/finance/${id}`, {
        ...data,
    });
}
